import React from 'react';
import { useLoyalty, REWARDS, TIERS } from '../context/LoyaltyContext';
import { PlatformLogo } from './PlatformLogo';
import { platforms } from '../mock/mock';
import { Sparkles, Trophy, Gift, Crown, Zap, Lock } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const LoyaltySection = () => {
  const { earned, balance, tierInfo, redeem, redemptions } = useLoyalty();
  const { current, next, progress } = tierInfo;

  const onRedeem = (reward) => {
    const res = redeem(reward);
    if (!res.ok) return toast({ title: 'Échange impossible', description: res.error });
    toast({ title: 'Récompense obtenue', description: `${reward.name} — livraison lancée !` });
  };

  return (
    <section className="mt-10 space-y-6">
      <div>
        <div className="text-[#ff8247] text-xs font-bold uppercase tracking-widest mb-3">Programme Fidélité</div>
        <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
          Boostez, gagnez, <span className="gradient-text">échangez</span>.
        </h2>
      </div>

      <div className="glass-card rounded-3xl p-6 lg:p-8 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full blur-3xl opacity-30" style={{ background: current.color }} />

        <div className="relative grid md:grid-cols-3 gap-6 items-center">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${current.color}25` }}>
                {current.id === 'platinum' ? <Crown className="w-6 h-6" style={{ color: current.color }} /> : <Trophy className="w-6 h-6" style={{ color: current.color }} />}
              </div>
              <div>
                <div className="text-white/50 text-xs uppercase tracking-widest font-semibold">Statut</div>
                <div className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{current.name}</div>
              </div>
            </div>
            <div className="text-white/60 text-xs">Avantage : {current.perk}</div>
          </div>

          <div className="md:border-l md:border-r border-white/10 md:px-6">
            <div className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-1">Solde de points</div>
            <div className="flex items-baseline gap-2">
              <Sparkles className="w-5 h-5 text-[#ff8247]" />
              <span className="text-4xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{balance.toLocaleString('fr-FR')}</span>
              <span className="text-white/50 text-sm">pts</span>
            </div>
            <div className="text-white/40 text-[11px] mt-1">Cumulés : {earned.toLocaleString('fr-FR')} pts</div>
          </div>

          <div>
            {next ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/50 text-xs uppercase tracking-widest font-semibold">Vers {next.name}</span>
                  <span className="text-white text-xs font-bold">{Math.max(0, next.min - earned).toLocaleString('fr-FR')} pts</span>
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-2">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${current.color}, ${next.color})` }} />
                </div>
                <div className="text-white/40 text-[11px]">1 € dépensé = 10 pts</div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-[#FFB300]">
                <Crown className="w-5 h-5" />
                <span className="text-sm font-semibold">Palier maximal atteint 🎉</span>
              </div>
            )}
          </div>
        </div>

        <div className="relative mt-6 flex gap-1.5">
          {TIERS.map((t, i) => {
            const active = earned >= t.min;
            return (
              <div key={t.id} className={`flex-1 h-1 rounded-full transition ${active ? '' : 'bg-white/5'}`} style={{ background: active ? t.color : undefined }} />
            );
          })}
        </div>
        <div className="relative mt-2 flex text-[10px] uppercase tracking-widest font-semibold">
          {TIERS.map((t) => (
            <div key={t.id} className="flex-1" style={{ color: earned >= t.min ? t.color : 'rgba(255,255,255,0.3)' }}>{t.name}</div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-4">
          <Gift className="w-5 h-5 text-[#ff8247]" />
          <h3 className="text-white text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Récompenses à échanger</h3>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {REWARDS.map(reward => {
            const plat = platforms.find(p => p.name === reward.platform);
            const canRedeem = balance >= reward.cost;
            return (
              <div key={reward.id} className={`glass-card rounded-2xl p-4 flex flex-col ${!canRedeem ? 'opacity-70' : ''}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${plat?.color || '#ff8247'}18` }}>
                    <PlatformLogo platform={plat?.id} className="w-6 h-6" color={plat?.id === 'instagram' ? undefined : (plat?.color || '#ff8247')} />
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#ff8247]/10 border border-[#ff8247]/20">
                    <Sparkles className="w-3 h-3 text-[#ff8247]" />
                    <span className="text-[#ff8247] text-xs font-bold">{reward.cost.toLocaleString('fr-FR')}</span>
                  </div>
                </div>
                <div className="text-white font-semibold text-sm mb-1">{reward.name}</div>
                <div className="text-white/50 text-xs mb-4 flex-1">Livré en 12h · sans mot de passe</div>
                <button onClick={() => onRedeem(reward)} disabled={!canRedeem}
                  className={`w-full py-2 rounded-full text-xs font-semibold transition inline-flex items-center justify-center gap-2 ${canRedeem ? 'bg-gradient-to-r from-[#ff8247] to-[#ff4d1a] text-[#0a0a0f] hover:shadow-lg hover:shadow-[#ff8247]/20' : 'bg-white/5 border border-white/10 text-white/40 cursor-not-allowed'}`}>
                  {canRedeem ? <><Zap className="w-3.5 h-3.5" /> Échanger</> : <><Lock className="w-3.5 h-3.5" /> {(reward.cost - balance).toLocaleString('fr-FR')} pts manquants</>}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {redemptions.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4 mt-2">
            <Trophy className="w-5 h-5 text-[#ff8247]" />
            <h3 className="text-white text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Historique des échanges</h3>
          </div>
          <div className="space-y-2">
            {redemptions.map(r => (
              <div key={r.id} className="glass-card rounded-xl p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#ff8247]/10 flex items-center justify-center"><Gift className="w-4 h-4 text-[#ff8247]" /></div>
                  <div>
                    <div className="text-white text-sm font-semibold">{r.name}</div>
                    <div className="text-white/40 text-xs">{new Date(r.createdAt).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                </div>
                <div className="text-[#ff8247] text-sm font-bold">−{r.cost.toLocaleString('fr-FR')} pts</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default LoyaltySection;
